import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiPages, useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Input, Select } from '../../engine/design/primitives/Input';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { CategoryBars, DayBars } from '../../engine/design/primitives/Viz';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { HtmlDocument } from '../../engine/fields/HtmlDocument';
import { useDebounced } from '../../engine/hooks/useDebounced';
import { formatDate } from '../../lib/format';

// Dashboards and reports for a tenant on a connected system of record (HaloPSA): its own dashboards
// (GET /v1/report-dashboards[/:id] — every widget's data, run by the API with the dashboard's report
// token) and its Report Center (GET /v1/report-center[/:id] — a saved report run as the signed-in
// user: chart + rows). Halo decides what the caller may see and run; nothing is computed here but
// the drawing.

export type ChartKind = 'bar' | 'line' | 'pie' | 'doughnut' | 'multi';
export interface ReportChart { kind: ChartKind; title: string | null; x: string | null; y: string | null; labels: string[]; values: number[]; series: Record<string, number[]> | null }
type Row = Record<string, unknown>;
interface Widget {
  id: number;
  title: string;
  kind: 'table' | 'chart' | 'counter' | 'html' | 'articles' | 'unsupported';
  error?: string | null;
  chart?: ReportChart | null;
  counter?: { value: number; aggregate: 'sum' | 'avg'; column: string | null; format: 'number' | 'money'; currency_symbol: string | null; breached: boolean };
  columns?: string[];
  rows?: Row[];
  truncated?: boolean;
  html?: string;
  articles?: { id: string; name: string }[];
}
interface Dashboard { id: string; name: string; widgets: Widget[] }
interface ReportRef { id: string; name: string; group_id: number | null; group_name: string | null; description: string | null }
interface Report extends ReportRef { chart: ReportChart | null; columns: { name: string; type: string }[]; rows: Row[]; row_count: number; truncated: boolean; period: { start: string | null; end: string | null } }

const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
/** A chart label for display: Halo sends dates as naive ISO datetimes. */
export const chartLabel = (l: string): string => (ISO.test(l) ? formatDate(l.slice(0, 10)) : l || '—');
const fmt = (n: number) => (Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));
const cellText = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'string' && ISO.test(v) ? formatDate(v.slice(0, 10)) : String(v));

/** A report's chart on the design-system visuals: bars/lines by period, pies as shares, several series as a table. */
export function ReportChartView({ chart, label }: { chart: ReportChart; label: string }) {
  if (chart.kind === 'multi' && chart.series) {
    const names = Object.keys(chart.series);
    const rows = chart.labels.map((l, i) => ({ key: String(i), label: chartLabel(l), ...Object.fromEntries(names.map((n) => [n, chart.series![n]![i] ?? 0])) }));
    const columns: GridColumn<Row>[] = [{ id: 'label', header: chart.x ?? '', accessor: (r) => r.label }, ...names.map((n) => ({ id: n, header: n, align: 'right' as const, accessor: (r: Row) => fmt(Number(r[n])) }))];
    return <DataGrid<Row> columns={columns} rows={rows} keyOf={(r) => String(r.key)} density="compact" />;
  }
  if (chart.values.length === 0) return <p className="hf-muted">No data for this period.</p>;
  const points = chart.labels.map((l, i) => ({ key: String(i), label: chartLabel(l), value: chart.values[i] ?? 0 }));
  const summary = `${label}: ${points.map((p) => `${p.label} ${fmt(p.value)}`).join(', ')}`;
  if (chart.kind === 'pie' || chart.kind === 'doughnut') {
    const total = points.reduce((s, p) => s + p.value, 0);
    return (
      <div role="img" aria-label={summary}>
        <CategoryBars items={[...points].sort((a, b) => b.value - a.value)} format={(n) => `${fmt(n)}${total ? ` · ${Math.round((n / total) * 100)}%` : ''}`} />
      </div>
    );
  }
  return (
    <div role="img" aria-label={summary} className="hf-report-chart">
      {points.length <= 31 ? <DayBars days={points} format={fmt} height={96} /> : <CategoryBars items={points} format={fmt} />}
    </div>
  );
}

function RowsTable({ columns, rows, truncated, total }: { columns: string[]; rows: Row[]; truncated?: boolean; total?: number }) {
  const cols: GridColumn<Row>[] = columns.map((c) => ({ id: c, header: c, accessor: (r) => cellText(r[c]) }));
  return (
    <div className="hf-stack" style={{ gap: 'var(--s-2)' }}>
      <DataGrid<Row> columns={cols} rows={rows.map((r, i) => ({ ...r, __k: i }))} keyOf={(r) => String(r.__k)} density="compact" empty={<p className="hf-muted">No rows for this period.</p>} />
      {truncated && <p className="hf-muted" role="note">Showing the first {rows.length}{total ? ` of ${total}` : ''} rows.</p>}
    </div>
  );
}

function WidgetCard({ w }: { w: Widget }) {
  const body = (() => {
    if (w.error) return <p className="hf-muted">{w.error}</p>;
    if (w.kind === 'counter' && w.counter) {
      const c = w.counter;
      const value = `${c.format === 'money' ? (c.currency_symbol ?? '') : ''}${c.format === 'money' ? c.value.toFixed(2) : fmt(c.value)}`;
      return (
        <div className="hf-stack" style={{ gap: 'var(--s-1)' }}>
          <span className="hf-num hf-widget-value">{value}</span>
          <span className="hf-row" style={{ gap: 'var(--s-2)' }}>
            {c.breached && <Badge tone="neg">Below threshold</Badge>}
            {c.column && <span className="hf-muted">{c.aggregate === 'avg' ? 'Average' : 'Total'} of {c.column}</span>}
          </span>
        </div>
      );
    }
    if (w.kind === 'chart') return w.chart ? <ReportChartView chart={w.chart} label={w.title} /> : <p className="hf-muted">No data for this period.</p>;
    if (w.kind === 'table') return <RowsTable columns={w.columns ?? []} rows={w.rows ?? []} truncated={w.truncated} />;
    if (w.kind === 'html') return <HtmlDocument html={w.html ?? ''} title={w.title || 'Widget'} maxHeight={320} />;
    if (w.kind === 'articles') return w.articles?.length ? <ul className="hf-plainlist">{w.articles.map((a) => <li key={a.id}>{a.name}</li>)}</ul> : <p className="hf-muted">No articles.</p>;
    return <p className="hf-muted">This kind of widget is shown in Halo only.</p>;
  })();
  return (
    <section className="hf-widget" aria-label={w.title || 'Widget'}>
      <h3 className="hf-section-title"><span>{w.title || 'Untitled widget'}</span></h3>
      {body}
    </section>
  );
}

function DashboardView({ id }: { id: string }) {
  const q = useApiQuery<Dashboard>(['report-dashboards', id], `/v1/report-dashboards/${id}`, { staleTime: 120_000 });
  if (q.isLoading) return <Skeleton lines={8} />;
  if (q.error || !q.data) return <ErrorBanner error={q.error} />;
  if (!q.data.widgets.length) return <EmptyState title="This dashboard has no widgets" icon="bar-chart" />;
  return (
    <div className="hf-widgets" role="list" aria-label={`${q.data.name} widgets`}>
      {q.data.widgets.map((w) => (
        <div role="listitem" key={w.id}>
          <WidgetCard w={w} />
        </div>
      ))}
    </div>
  );
}

function ReportView({ id, onBack }: { id: string; onBack: () => void }) {
  const q = useApiQuery<Report>(['report-center', id], `/v1/report-center/${id}`, { staleTime: 120_000 });
  const r = q.data;
  return (
    <div className="hf-stack">
      <div className="hf-row" style={{ gap: 'var(--s-2)' }}>
        <Button size="sm" variant="ghost" icon="arrow-left" onClick={onBack}>All reports</Button>
      </div>
      {q.isLoading ? (
        <Skeleton lines={8} />
      ) : q.error || !r ? (
        <ErrorBanner error={q.error} />
      ) : (
        <section className="hf-stack" aria-label={r.name}>
          <h2 className="hf-section-title">
            <span>{r.name}</span>
            {r.group_name && <span className="hf-muted">{r.group_name}</span>}
          </h2>
          {(r.period.start || r.period.end) && <p className="hf-muted">{formatDate(r.period.start)} – {formatDate(r.period.end)}</p>}
          {r.chart && <ReportChartView chart={r.chart} label={r.name} />}
          <RowsTable columns={r.columns.map((c) => c.name)} rows={r.rows} truncated={r.truncated} total={r.row_count} />
        </section>
      )}
    </div>
  );
}

function ReportCenter() {
  const [sp, setSp] = useSearchParams();
  const open = sp.get('report');
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 300);
  const [group, setGroup] = useState('');
  const list = useApiPages<ReportRef>(['report-center', 'list', q], '/v1/report-center', { query: { limit: 200, q: q || undefined } });
  const groups = useMemo(() => [...new Map(list.rows.filter((r) => r.group_name).map((r) => [String(r.group_id), r.group_name!])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [list.rows]);
  const rows = group ? list.rows.filter((r) => String(r.group_id) === group) : list.rows;
  const setReport = (id: string | null) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      if (id) n.set('report', id);
      else n.delete('report');
      return n;
    });
  if (open) return <ReportView id={open} onBack={() => setReport(null)} />;
  const columns: GridColumn<ReportRef>[] = [
    { id: 'name', header: 'Report', accessor: (r) => r.name, width: 440, minWidth: 220 },
    { id: 'group', header: 'Group', accessor: (r) => r.group_name ?? '', width: 280 },
  ];
  return (
    <div className="hf-stack">
      <div className="hf-row" style={{ gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <Input size="sm" type="search" placeholder="Find a report" aria-label="Find a report" value={text} onChange={(e) => setText(e.target.value)} />
        <Select size="sm" aria-label="Report group" value={group} onChange={(e) => setGroup(e.target.value)} options={[{ value: '', label: 'All groups' }, ...groups.map(([value, label]) => ({ value, label }))]} />
      </div>
      <DataGrid<ReportRef>
        columns={columns}
        rows={rows}
        keyOf={(r) => r.id}
        loading={list.isLoading}
        error={list.error}
        hasMore={list.hasMore}
        loadMore={list.loadMore}
        loadingMore={list.loadingMore}
        onRowClick={(r) => setReport(r.id)}
        empty={<EmptyState title="No reports" hint="No saved reports you can run match." icon="bar-chart" />}
      />
    </div>
  );
}

const REPORTS_TAB = 'reports';

/** The Dashboards screen for a tenant on a connected system: its dashboards as tabs, then its Report Center. */
export function SourceDashboards({ system }: { system: string }) {
  const [sp, setSp] = useSearchParams();
  const list = useApiQuery<{ data: { id: string; name: string }[] }>(['report-dashboards', 'list'], '/v1/report-dashboards', { staleTime: 300_000, query: { limit: 100 } });
  const dashboards = list.data?.data ?? [];
  const tab = sp.get('tab');
  const active = tab === REPORTS_TAB || sp.get('report') ? REPORTS_TAB : (dashboards.find((d) => d.id === tab)?.id ?? dashboards[0]?.id ?? (list.isLoading ? null : REPORTS_TAB));
  const tabs = [...dashboards.map((d) => ({ key: d.id, label: d.name })), { key: REPORTS_TAB, label: 'Report Center' }];
  return (
    <div className="hf-page">
      <PageHeader title="Dashboards" subtitle={`Dashboards and reports from ${system}, as you see them there`} />
      {list.error && <p className="hf-muted" role="note">{system}'s dashboards could not be loaded; its Report Center is below.</p>}
      <Tabs
        variant="pills"
        aria-label="Dashboard"
        value={active ?? ''}
        onChange={(key) =>
          setSp((p) => {
            const n = new URLSearchParams(p);
            n.set('tab', key);
            n.delete('report');
            return n;
          }, { replace: true })
        }
        tabs={tabs}
      />
      <div className="hf-tabpanel" role="tabpanel" aria-label={tabs.find((t) => t.key === active)?.label ?? 'Dashboard'}>
        {active === null ? <Skeleton lines={6} /> : active === REPORTS_TAB ? <ReportCenter /> : <DashboardView id={active} />}
      </div>
    </div>
  );
}
